import React, { useRef } from 'react';
import { Controller, UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { components, SingleValueProps } from 'react-select';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Dot } from '../../../../../../CommonComponents/OPL/Dot/Dot';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { formatNumber, formatPercentage } from '../../../../../../CommonUtils/formatNumber';
import { StackedBar } from '../../../../../../CommonComponents/OPL/charts/StackedBar/StackedBar';
import { HelperText } from '../../../../../../CommonComponents/OPL/HelperText/HelperText';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { SearchSymbolsSelect } from './components/SearchSymbolsSelect/SearchSymbolsSelect';
import { searchSymbols } from './components/SearchSymbolsSelect/useSearchSymbolsSelect';
import { applyProductAssetAllocation } from './components/SearchSymbolsSelect/applyProductAssetAllocation';
import { Toggle } from '../../../../../../CommonComponents/OPL/Toggle/Toggle';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { SymbolOption } from './components/SearchSymbolsSelect/useSearchSymbolsSelect';
import { TotalFooter } from './UpsertHoldingModal.styles';
import { FormFields } from '../../../AccountPreferences/components/AccountForm/AccountForm.meta';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { useUpsertHoldingModal } from './useUpsertHoldingModal';

const SingleValue = ({ children, ...rest }: SingleValueProps<any>) => {
  const text = rest.hasValue
    ? `${rest.data.label}${rest.data.subLabel ? ` • ${rest.data.subLabel}` : ''}`
    : children;

  return (
    <components.SingleValue {...rest}>
      <Text fontWeight={500}>{text}</Text>
    </components.SingleValue>
  );
};

interface Props {
  holdingIndex: number;
  onClose(): void;
  availableAssetBuckets: { value: number; label: string }[];
  holdingsArrayFormBag: UseFieldArrayReturn<FormFields, 'bankAccount.Positions', 'key'>;
}

export const UpsertHoldingModal = ({
  holdingIndex,
  onClose,
  availableAssetBuckets,
  holdingsArrayFormBag,
}: Props) => {
  const formContext = useFormContext<FormFields>();

  const clientId = useClientId();

  const { fields: holdings, remove } = holdingsArrayFormBag;
  const holding = holdings[holdingIndex];

  return (
    <Modal
      isOpen
      size="large"
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'upsert-holding--modal-header' }}
      key={holding.key}
    >
      <ModalHeader as="h2" id="upsert-holding--modal-header">
        {holding.Id ? 'Edit' : 'Add'} Holding
      </ModalHeader>

      <ModalCloseButton
        onClick={() => {
          if (!holding.Id) {
            remove(holdingIndex);
          }

          formContext.resetField(`bankAccount.Positions.${holdingIndex}.AssetAllocation`);

          onClose();
        }}
      />

      <UpsertHoldingModalContent
        clientId={clientId}
        holdingIndex={holdingIndex}
        holdingsArrayFormBag={holdingsArrayFormBag}
        onClose={onClose}
        availableAssetBuckets={availableAssetBuckets}
      />
    </Modal>
  );
};

interface ContentProps {
  clientId: number;
  onClose(): void;
  availableAssetBuckets: { value: number; label: string }[];
  holdingsArrayFormBag: UseFieldArrayReturn<FormFields, 'bankAccount.Positions', 'key'>;
  holdingIndex: number;
}

const UpsertHoldingModalContent = ({
  clientId,
  onClose: onBaseClose,
  availableAssetBuckets,
  holdingsArrayFormBag,
  holdingIndex,
}: ContentProps) => {
  const formContext = useFormContext<FormFields>();
  const isPriceSetFromSearchRef = useRef(false);

  const {
    data: {
      allocationColors,
      allocationTotal,
      allowAssetBucketingCustomization,
      assetAllocation,
      controlledAllocationFields,
      hasLatestMarketPrice,
      holding,
      isAllocationInvalid,
      isPriceManuallyEntered,
      selectedSymbolOption,
      showAddManualSymbol,
      showSymbolAllocationWarning,
      showTaxStatusOption,
      stackedBarData,
      taxStatusOptions,
      ticker,
      price,
      quantity,
      useLatestMarketPrice,
      assetBucketingByManagementStyle,
    },
    actions: {
      onClose,
      onReset,
      onSave,
      setSelectedSymbolOption,
      setShowAddManualSymbol,
      setShowSymbolAllocationWarning,
    },
  } = useUpsertHoldingModal({
    holdingIndex,
    holdingsArrayFormBag,
    onBaseClose,
  });

  return (
    <>
      <div css="overflow-y: auto">
        {showSymbolAllocationWarning && (
          <Alert
            variant="warning"
            message="You are changing the asset allocation of a known position. Do you want to continue?"
            mx="24"
            mb="16"
          />
        )}

        {!holding.Id && (
          <Box my="16" mx="24">
            <SearchSymbolsSelect
              clientId={clientId}
              label="Symbol/Name"
              value={selectedSymbolOption}
              onChange={opt => {
                setSelectedSymbolOption(opt);

                if (opt) {
                  const { data } = opt as SymbolOption;

                  isPriceSetFromSearchRef.current = true;
                  formContext.setValue(`bankAccount.Positions.${holdingIndex}.Ticker`, data.ticker);
                  formContext.setValue(`bankAccount.Positions.${holdingIndex}.Name`, data.name);
                  formContext.setValue(
                    `bankAccount.Positions.${holdingIndex}.ProductId`,
                    data.productId,
                  );
                  formContext.setValue(`bankAccount.Positions.${holdingIndex}.Price`, data.price);
                  formContext.setValue(
                    `bankAccount.Positions.${holdingIndex}.IsPriceManuallyEntered`,
                    false,
                  );
                  const assetAllocationList = applyProductAssetAllocation(
                    assetAllocation,
                    data.assetClasses,
                    data.assetClass,
                  );

                  formContext.setValue(
                    `bankAccount.Positions.${holdingIndex}.AssetAllocation`,
                    assetAllocationList,
                  );
                }
              }}
              placeholder="Search..."
              openMenuOnClick={false}
              disabled={showAddManualSymbol}
              components={{ DropdownIndicator: () => null, SingleValue }}
              isClearable
              formVariant={
                formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Name && 'error'
              }
              helperText={
                formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Name?.message
              }
            />

            {!showAddManualSymbol && (
              <Button
                variant="link"
                leftIcon="fas fa-plus"
                onClick={() => {
                  setShowAddManualSymbol(true);
                  setSelectedSymbolOption(null);
                  setShowSymbolAllocationWarning(false);
                  formContext.setValue(
                    `bankAccount.Positions.${holdingIndex}.IsPriceManuallyEntered`,
                    true,
                  );
                }}
                mt="4"
              >
                Add New Symbol/Name
              </Button>
            )}
          </Box>
        )}

        {showAddManualSymbol && (
          <Box
            css={`
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 16px;
              margin: 16px 24px;
            `}
          >
            <Input
              label="Symbol"
              {...formContext.register(`bankAccount.Positions.${holdingIndex}.Ticker`)}
              readOnly={!!holding.ProductId}
              formVariant={
                formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Ticker &&
                'error'
              }
              helperText={
                formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Ticker?.message
              }
            />

            <Box css="grid-column: 2 / -1">
              <Box display="flex" alignItems="center" css="gap: 16px">
                <Input
                  label="Name"
                  {...formContext.register(`bankAccount.Positions.${holdingIndex}.Name`)}
                  readOnly={!!holding.ProductId}
                  css="flex: 1"
                  formVariant={
                    formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Name &&
                    'error'
                  }
                  helperText={
                    formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Name
                      ?.message
                  }
                />

                {!holding.Id && (
                  <IconButton
                    icon="far fa-minus-circle"
                    variant="subtleInactive"
                    onClick={() => setShowAddManualSymbol(false)}
                    aria-label="Delete symbol"
                    mt="24"
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Box
          css={`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin: 16px 24px 24px;
          `}
        >
          <Controller
            control={formContext.control}
            name={`bankAccount.Positions.${holdingIndex}.Quantity`}
            render={({ field: { value, onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Quantity"
                value={value}
                allowNegative={false}
                onValueChange={({ floatValue }) => {
                  if (price) {
                    formContext.setValue(
                      `bankAccount.Positions.${holdingIndex}.MarketValue`,
                      price * (floatValue ?? 0),
                    );
                  }

                  onChange(floatValue ?? null);
                }}
                formVariant={
                  formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Quantity &&
                  'error'
                }
                helperText={
                  formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Quantity
                    ?.message
                }
              />
            )}
          />

          <Box css="display: flex; flex-direction: column; gap: 8px">
            <Controller
              control={formContext.control}
              name={`bankAccount.Positions.${holdingIndex}.Price`}
              render={({ field: { value, onChange, ...field } }) => (
                <InputNumberMask
                  {...field}
                  label="Price"
                  value={value}
                  decimalScale={2}
                  allowNegative={false}
                  leftIcon="far fa-dollar-sign"
                  readOnly={useLatestMarketPrice}
                  onValueChange={({ floatValue }) => {
                    const inputPrice = floatValue ?? null;
                    onChange(inputPrice);

                    if (isPriceSetFromSearchRef.current) {
                      isPriceSetFromSearchRef.current = false;
                    } else {
                      formContext.setValue(
                        `bankAccount.Positions.${holdingIndex}.IsPriceManuallyEntered`,
                        true,
                      );
                    }

                    if (quantity && inputPrice) {
                      formContext.setValue(
                        `bankAccount.Positions.${holdingIndex}.MarketValue`,
                        inputPrice * (quantity ?? 0),
                      );
                    }
                  }}
                  formVariant={
                    formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Price &&
                    'error'
                  }
                  helperText={
                    formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]?.Price
                      ?.message
                  }
                />
              )}
            />
            <Toggle
              checked={useLatestMarketPrice}
              disabled={!hasLatestMarketPrice}
              onChange={async e => {
                const useLatest = e.target.checked;
                isPriceSetFromSearchRef.current = useLatest;
                formContext.setValue(
                  `bankAccount.Positions.${holdingIndex}.IsPriceManuallyEntered`,
                  !useLatest,
                );
                if (useLatest) {
                  let latestPrice: number | null = null;

                  if (selectedSymbolOption) {
                    latestPrice = selectedSymbolOption.data.price;
                  } else if (holding.Id && holding.ProductId && holding.Ticker) {
                    const symbols = await searchSymbols({
                      clientId,
                      search: holding.Ticker,
                    });
                    const matchingProduct = symbols.find(
                      s =>
                        s.data.productId === holding.ProductId || s.data.ticker === holding.Ticker,
                    );
                    latestPrice = matchingProduct?.data?.price ?? null;
                  }

                  if (latestPrice != null) {
                    formContext.setValue(
                      `bankAccount.Positions.${holdingIndex}.Price`,
                      latestPrice,
                    );
                    if (quantity) {
                      formContext.setValue(
                        `bankAccount.Positions.${holdingIndex}.MarketValue`,
                        latestPrice * (quantity ?? 0),
                      );
                    }
                  }
                }
              }}
              aria-label="Set to latest market price"
            >
              Set to latest market price
            </Toggle>
          </Box>

          <Controller
            control={formContext.control}
            name={`bankAccount.Positions.${holdingIndex}.MarketValue`}
            render={({ field }) => {
              return (
                <InputNumberMask
                  {...field}
                  label="Market Value"
                  decimalScale={2}
                  allowNegative={false}
                  leftIcon="far fa-dollar-sign"
                  readOnly
                />
              );
            }}
          />
        </Box>

        <Box
          css={`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin: 16px 24px 24px;
          `}
        >
          {allowAssetBucketingCustomization && !assetBucketingByManagementStyle && (
            <Controller
              control={formContext.control}
              name={`bankAccount.Positions.${holdingIndex}.AssetBucketIndex`}
              render={({ field: { value, onChange, ref } }) => {
                return (
                  <Select
                    innerRef={ref}
                    label="Move Holding To"
                    value={availableAssetBuckets.find(({ value: v }) => v === value)}
                    options={availableAssetBuckets}
                    onChange={opt => onChange(opt?.value)}
                    formVariant={
                      formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]
                        ?.AssetBucketIndex && 'error'
                    }
                    helperText={
                      formContext.formState.errors.bankAccount?.Positions?.[holdingIndex]
                        ?.AssetBucketIndex?.message
                    }
                  />
                );
              }}
            />
          )}

          {showTaxStatusOption && (
            <Controller
              control={formContext.control}
              name={`bankAccount.Positions.${holdingIndex}.TaxStatus`}
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  innerRef={ref}
                  label="Select tax status"
                  isSearchable={false}
                  options={taxStatusOptions}
                  value={taxStatusOptions.find(option => option.value === value) ?? null}
                  onChange={opt => onChange(opt?.value ?? null)}
                  isPortal
                  isClearable
                />
              )}
            />
          )}
        </Box>

        <Box m="24">
          <Box display="flex" alignItems="center" justifyContent="space-between" mb="8">
            <Text fontWeight={600}>Asset Allocation</Text>

            <Button
              variant="link"
              onClick={() => {
                onReset();
              }}
            >
              Reset
            </Button>
          </Box>

          <StackedBar
            data={stackedBarData}
            formatter={value => formatPercentage((value || 0) / 100, { decimalPlaces: 2 })}
          />
        </Box>

        <HtmlTable variant="embedded" css="overflow-y: unset; border-bottom: none" edgePadding={24}>
          <table>
            <colgroup>
              <col width="100%" />
              <col width="0%" />
            </colgroup>

            <thead>
              <tr>
                <th>
                  <Text variant="smallCaps" textAlign="left">
                    Asset Class
                  </Text>
                </th>
                <th>
                  <Text variant="smallCaps" textAlign="right">
                    Allocation
                  </Text>
                </th>
              </tr>
            </thead>

            <tbody>
              {controlledAllocationFields.map((asset, index) => (
                <tr key={`${asset.id}-${ticker}`}>
                  <td>
                    <Box display="flex" alignItems="center">
                      <Dot bg={allocationColors[index]} mr="8" />
                      <Text fontWeight={500}>{asset.name}</Text>
                    </Box>
                  </td>

                  <td>
                    <Box width={154}>
                      <Controller
                        control={formContext.control}
                        name={`bankAccount.Positions.${holdingIndex}.AssetAllocation.${index}.y`}
                        render={({ field: { value, onChange, ...field } }) => {
                          return (
                            <InputNumberMask
                              {...field}
                              value={value}
                              onValueChange={({ floatValue }) => {
                                if (selectedSymbolOption) {
                                  setShowSymbolAllocationWarning(true);
                                }

                                onChange(floatValue ?? 0);
                              }}
                              decimalScale={2}
                              allowNegative={false}
                              rightIcon="far fa-percent"
                              aria-label="Allocation"
                              style={{ textAlign: 'right' }}
                            />
                          );
                        }}
                      />
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </HtmlTable>

        <TotalFooter>
          <Box display="flex" justifyContent="space-between">
            <Text fontWeight={600}>Total</Text>

            <Text fontWeight={600}>
              {formatNumber(allocationTotal, {
                decimalPlaces: 2,
                thousandSeparated: false,
              })}
              %
            </Text>
          </Box>

          {isAllocationInvalid && (
            <Box alignSelf="flex-end">
              <HelperText variant="error" mt="4">
                The total must add up to 100%
              </HelperText>
            </Box>
          )}
        </TotalFooter>
      </div>

      <ModalFooter>
        <Button
          variant="subtleInactive"
          onClick={() => {
            onClose();
          }}
          mr="8"
        >
          Cancel
        </Button>

        <Button
          onClick={() => {
            onSave();
          }}
        >
          {holding.Id ? 'Save' : 'Add'} Holding
        </Button>
      </ModalFooter>
    </>
  );
};
